import * as THREE from "three";

import { Vector2 } from "../../../../common/vector2.js";
import { Game } from "../../../../game.js";
import { ImgID } from "../../../../image_loader/img_id.js";
import { DAILY_CHALLENGE_DAYS_COUNT } from "../../../../daily_challenge/daily_challenge_config.js";
import { ScreenManager, Screens } from "../../../../screen_manager/screen_manager.js";
import { BasicEventsController } from "../../../../touch_events_controller/basic_events_controller.js";
import { ColorScheme } from "../../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../../common_ui_elements/ui_text_element.js";
import { GameScreen } from "../game_screen.js";
import { GameSettingsMenu } from "./game_settings_menu.js";
import { GameTutorial } from "./tutorial/game_tutorial.js";
import { InvalidShotMessage } from "./invalid_shot_message.js";
import { PlayDialogDifficulty } from "../../../dialog/play_dialog.js";
import { Easing } from "../../../../transforms/transform_base.js";
import { TransformStraightMove } from "../../../../transforms/transform_straight_move.js";
import { TransformOpacity } from "../../../../transforms/transform_opacity.js";
import { TransformScale } from "../../../../transforms/transform_scale.js";
import { audioController } from "../../../../audio/audio_controller.js";
import { AudioClips } from "../../../../audio/audio_constants.js";
import { LocalizationManager } from "../../../../localization/localization_manager.js";
import { StatisticsStorage } from "../../../../storage/statistics_storage.js";
import { BuyBoosterDialog } from "../../../dialog/buy_booster_dialog.js";
import { BuyBoosterType, getBoosterTextVariableParams } from "../../../dialog/buy_booster_types.js";
import { DialogManager } from "../../../dialog/dialog_manager.js";
import { BoosterHighlightRenderer } from "../../../overlay/booster_highlight_renderer.js";
import { CumulativeBoosterID } from "../../../../cumulative_boosters/cumulative_booster_id.js";
import { CumulativeBoosterService } from "../../../../cumulative_boosters/cumulative_booster_service.js";
import { OverlayFactory } from "../../../overlay/overlay_factory.js";
import { OverlayId } from "../../../overlay/overlay_ids.js";
import { OverlayManager } from "../../../overlay/overlay_manager.js";
import { Vfx2DManager } from "../../../../webgl_2d/vfx_2d_manager.js";
import { IController } from "../../../../webgl_2d/core/types/controller_types.js";
import { TelegramAPISettings } from "../../../../telegram_api_settings.js";

export enum GameBoosterState
{
  Available = "available",
  Unavailable = "unavailable",
  Locked = "locked",
}

interface GameBoosterSlotConfig
{
  x: number;
  y: number;
  icon: ImgID;
  unlock_level: number;
  booster_type: BuyBoosterType;
  booster_id: CumulativeBoosterID;
}

interface BoosterTooltipTextConfig
{
  title_key: string;
  description_key: string;
}

const BOOSTER_TOOLTIP_TEXT: Record<BuyBoosterType, BoosterTooltipTextConfig> = {
  [BuyBoosterType.Firework]: {
    title_key: "BOOSTER.FIREWORK",
    description_key: "DESCRIPTION.FIREWORK",
  },
  [BuyBoosterType.FiveBalls]: {
    title_key: "BOOSTER.FIVE_BALLS",
    description_key: "DESCRIPTION.FIVE_BALLS",
  },
  [BuyBoosterType.BigBall]: {
    title_key: "BOOSTER.BIG_BALL",
    description_key: "DESCRIPTION.BIG_BALL",
  },
  [BuyBoosterType.InfiniteShots]: {
    title_key: "BOOSTER.INFINITE_SHOTS",
    description_key: "DESCRIPTION.INFINITE_SHOTS",
  },
};

export class GameUI extends UIElement
{
  private static readonly BOOSTER_TOOLTIP_HOLD_MS = 2000;
  private static readonly BOOSTER_UNLOCK_TUTORIAL_ENABLED = true;
  private static readonly BOOSTER_UNLOCK_TUTORIALS = [
    { booster_id: CumulativeBoosterID.Multiball, booster_type: BuyBoosterType.FiveBalls },
    { booster_id: CumulativeBoosterID.Fireworks, booster_type: BuyBoosterType.Firework },
    { booster_id: CumulativeBoosterID.BigBall, booster_type: BuyBoosterType.BigBall },
    { booster_id: CumulativeBoosterID.InfiniteBalls, booster_type: BuyBoosterType.InfiniteShots },
  ] as const;
  private static readonly SHOTS_PULSE_LOW_THRESHOLD = 5;
  private static readonly SHOTS_PULSE_SCALE = 1.08;
  private static readonly SHOTS_PULSE_DURATION_MS = 700;
  private static readonly SHOTS_PULSE_SETTLE_MS = 150;

  private events_controller!: BasicEventsController;

  private game_screen: GameScreen;
  private shots_text!: UITextElement;
  private shots_bg!: UIImage;
  private level_bg!: UIImage;
  private level_text!: UITextElement;
  private difficulty_text!: UITextElement;
  private settings_button!: UIImage;
  private settings_menu!: GameSettingsMenu;
  private booster_layer!: UIElementHolder;
  private booster_hit_zones: UIElementHolder[] = [];
  private static readonly BOOSTER_DESIGN_SIZE = 175.5;
  private static readonly BOOSTER_ICON_OVERHANG = 19.8;
  private static readonly BOOSTER_BADGE_OVERHANG = 36;
  private static readonly BOOSTER_MIN_BOTTOM_MARGIN = 24;
  private static readonly BOOSTER_FIT_INSET = 0.1;
  private static readonly BOOSTER_FIT_STEPS = [
    { scale: 1, margin: 1 },
    { scale: 0.9, margin: 1 },
    { scale: 0.8, margin: 0.75 },
  ] as const;

  private booster_fit_step = 0;
  private booster_hit_zones_by_id = new Map<CumulativeBoosterID, UIElementHolder>();
  private booster_tooltip_targets_by_id = new Map<CumulativeBoosterID, {
    config: GameBoosterSlotConfig;
    state: GameBoosterState;
  }>();
  private booster_glows = new Map<CumulativeBoosterID, IController>();
  private booster_locked_tooltip: UIElementHolder | null = null;
  private booster_locked_dismiss_layer: UIElementHolder | null = null;
  private booster_long_press: {
    hit_zone: UIElementHolder;
    config: GameBoosterSlotConfig;
    state: GameBoosterState;
    timer: number | null;
    triggered: boolean;
  } | null = null;
  private invalid_shot_message!: InvalidShotMessage;
  private shots_pulse_active = false;
  private booster_unlock_overlay_requested = false;
  private readonly booster_unlock_ready_at = performance.now() + 400;
  private top_hud_reveal_at = 0;
  private top_hud_reveal_started = false;
  private difficulty_label_should_show = false;
  private difficulty_compact_pos = new Vector2( 0, 0 );
  private difficulty_intro_started = false;
  private difficulty_intro_pending = false;

  private is_challenge = false;
  private header_created = false;

  private tutorial!: GameTutorial;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, game_screen: GameScreen )
  {
    super( ctx, x, y, width, height, false, false );

    this.initializeEventsController( ctx.canvas );

    this.game_screen = game_screen;

    this.createShotsLeft();
    this.createBoosterBackgrounds();
    this.createSettings();
    this.createTutorial();

    requestAnimationFrame( () => this.invalidateSubtree() );

    this.invalid_shot_message = new InvalidShotMessage( this.rect.getW(), this.rect.getH() );

    this.updateShotsPulse( this.game_screen.getShotsLeft() );

    this.game_screen.onShotsLeftChanged = ( shots_left: number ) =>
    {
      this.shots_text.setText( shots_left.toString() );
      this.updateShotsPulse( shots_left );
      this.tutorial.onShotFired();
      if ( !this.game_screen.shouldHighlightBoosters() )
      {
        this.clearBoosterGlows();
      }
    };

    this.game_screen.onAttemptFinished = () =>
    {
      this.clearBoosterGlows();
    };

    this.game_screen.onLevelBuilt = () => this.fitBoostersToBlocks();

    this.game_screen.onDifficultyChanged = ( difficulty: PlayDialogDifficulty, level: number ) =>
    {
      this.ensureHeaderCreated( level );
      this.createBoosterBackgrounds();
      this.shots_bg.setImageId( this.getShotsBgImgId( difficulty ) );
      this.settings_button.setImageId( this.getSettingsImgId( difficulty ) );

      if ( this.is_challenge )
      {
        this.shots_text.setOutline(
          LayoutScaler.widthFromDesign( 13 ),
          ColorScheme.daily_challenge_level_outline,
          0,
          LayoutScaler.yFromDesign( 3 )
        );
        return;
      }

      this.level_bg.setImageId( this.getLevelTapeImgId( difficulty ) );
      const level_outline = this.getLevelOutlineColor( difficulty );
      this.level_text.setOutline(
        LayoutScaler.widthFromDesign( 8 ),
        level_outline,
        0,
        LayoutScaler.yFromDesign( 3 )
      );
      this.difficulty_text.setOutline(
        LayoutScaler.widthFromDesign( 7 ),
        level_outline,
        0,
        LayoutScaler.yFromDesign( 3 )
      );
      this.shots_text.setOutline(
        LayoutScaler.widthFromDesign( 13 ),
        level_outline,
        0,
        LayoutScaler.yFromDesign( 3 )
      );
      this.level_text.setText( `${LocalizationManager.t( "LEVEL" )} ${level}` );
      this.difficulty_text.setText( this.getDifficultyLabel( difficulty ) );
      this.difficulty_label_should_show = difficulty !== PlayDialogDifficulty.Common;
      if ( !this.difficulty_label_should_show )
      {
        this.difficulty_intro_pending = false;
        this.difficulty_text.setOpacityImmediate( 0 );
      }
      else
      {
        this.difficulty_intro_pending = true;
        this.difficulty_text.setOpacityImmediate( 0 );
      }
    }

    this.game_screen.onInvalidShot = ( event: PointerEvent ) =>
    {
      this.showInvalidShotMessage( event.clientX, event.clientY );
    }

    this.game_screen.onAimStart = () =>
    {
      return this.tutorial.onAimStart();
    }

    this.game_screen.onBoosterCountChanged = () =>
    {
      this.scheduleBoosterRefresh();
    };

    this.game_screen.onResultOverlayShown = () =>
    {
      this.hideTopHudForResultOverlay();
    };

    const coin_fly_finish_at = Vfx2DManager.getLatestCoinFlyFinishTime();
    if ( coin_fly_finish_at > performance.now() )
    {
      this.top_hud_reveal_at = coin_fly_finish_at;
      // The coin counter itself is CoinFlightHud, drawn above this screen.
      this.hideTopHudForResultOverlay();
    }
  }

  private ensureHeaderCreated( level: number ): void
  {
    if ( this.header_created )
      return;

    this.header_created = true;
    this.is_challenge = this.game_screen.isChallenge();

    if ( this.is_challenge )
    {
      this.createChallengeHeader( level );
    } else
    {
      this.createLevelIndicator();
    }

    if ( this.top_hud_reveal_at !== 0 && !this.top_hud_reveal_started )
    {
      this.level_bg.setOpacityImmediate( 0 );
      this.level_bg.setClickable( false );
      this.difficulty_text?.setOpacityImmediate( 0 );
    }
  }

  private createChallengeHeader( num: number ): void
  {
    const bg_w = LayoutScaler.widthFromDesign( 488 );
    const bg_h = LayoutScaler.heightFromDesign( 205 );

    const bg_y = LayoutScaler.yFromDesign( 68 );
    const bg_x = ( this.rect.getW() - bg_w ) / 2;

    const bg = new UIImage( this.ctx, bg_x, bg_y, bg_w, bg_h, ImgID.UI_GameDailyChallengeHeader );
    this.addChild( bg );

    this.level_bg = bg;

    const text_w = LayoutScaler.widthFromDesign( 120 );
    const text_h = LayoutScaler.heightFromDesign( 50 );

    const text_y = LayoutScaler.yFromDesign( 147 );
    const text_x = ( bg_w - text_w ) / 2;

    const text = new UITextElement(
      this.ctx, text_x, text_y, text_w, text_h,
      {
        text: `${num.toString()}/${DAILY_CHALLENGE_DAYS_COUNT}`,
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( 35 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: ColorScheme.daily_challenge_level_outline,
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
        ...TextStyleScheme.buttonShadow(),
      }
    )

    bg.addChild( text );
  }

  private hideTopHudForResultOverlay(): void
  {
    this.settings_menu.close();

    // difficulty_text is never created in challenge mode.
    const elements: ( UIElement | undefined )[] = [
      this.shots_bg,
      this.level_bg,
      this.difficulty_text,
      this.settings_button,
    ];

    for ( const element of elements )
    {
      if ( !element )
        continue;

      element.stopAllTransforms()
      element.setOpacityImmediate( 0 );
      element.setClickable( false );
    }

    this.forceMakeShouldBeRendered();
  }

  private revealTopHudAfterCoinFlight(): void
  {
    if ( this.top_hud_reveal_started )
      return;

    this.top_hud_reveal_started = true;
    const duration = 260;
    const elements: UIElement[] = [
      this.shots_bg,
      this.level_bg,
      this.settings_button,
    ];

    const now = performance.now();
    for ( const element of elements )
      this.animateTopHudElementIn( element, now, duration );

    this.settings_button.setClickable( true );
    this.forceMakeShouldBeRendered();
  }

  private animateTopHudElementIn( element: UIElement, start_time: number, duration: number ): void
  {
    const bump_duration = Math.round( duration * 0.7 );
    const settle_duration = duration - bump_duration;

    element.setOpacityImmediate( 0 );
    element.setScaleImmediate( new Vector2( 0.5, 0.5 ) );
    element.addTransform( new TransformOpacity(
      element,
      true,
      0,
      1,
      start_time,
      duration,
      { easing: Easing.EaseOutQuad }
    ) );
    element.addTransform( new TransformScale(
      element,
      true,
      new Vector2( 0.5, 0.5 ),
      new Vector2( 1.1, 1.1 ),
      start_time,
      bump_duration,
      { easing: Easing.EaseOutQuad }
    ) );
    element.addTransform( new TransformScale(
      element,
      true,
      new Vector2( 1.1, 1.1 ),
      new Vector2( 1, 1 ),
      start_time + bump_duration,
      settle_duration,
      { easing: Easing.EaseInOutQuad }
    ) );
  }

  private startDifficultyIntro( now: number ): void
  {
    if ( this.difficulty_intro_started || OverlayManager.isAnyOverlayOpened() )
      return;

    this.difficulty_intro_started = true;
    this.difficulty_intro_pending = false;

    const intro_center_x = this.rect.getW() / 2;
    const intro_center_y = LayoutScaler.yFromDesign( 336 );
    const intro_pos = new Vector2(
      intro_center_x - this.difficulty_text.getRect().getW() / 2,
      intro_center_y - this.difficulty_text.getRect().getH() / 2
    );
    const move_start = now + 500;
    const move_duration = 150;

    this.difficulty_text.stopAllTransforms();
    this.difficulty_text.setPixelPos( intro_pos, true );
    this.difficulty_text.setScaleImmediate( new Vector2( 2.25, 2.25 ) );
    this.difficulty_text.setOpacityImmediate( 1 );
    this.difficulty_text.addTransform( new TransformStraightMove(
      this.difficulty_text,
      true,
      intro_pos,
      this.difficulty_compact_pos,
      move_start,
      move_duration,
      { easing: Easing.EaseInOutQuad }
    ) );
    this.difficulty_text.addTransform( new TransformScale(
      this.difficulty_text,
      true,
      new Vector2( 2.25, 2.25 ),
      new Vector2( 1, 1 ),
      move_start,
      move_duration,
      { easing: Easing.EaseInOutQuad }
    ) );
    this.forceMakeShouldBeRendered();
  }

  private createSettings(): void
  {
    const w = LayoutScaler.widthFromDesign( 150 );
    const h = LayoutScaler.heightFromDesign( 150 );
    const x = this.rect.getW() - w - LayoutScaler.xFromDesign( 50 );
    const y = LayoutScaler.yFromDesign( 87 );

    const button = new UIImage(
      this.ctx,
      x,
      y,
      w,
      h,
      ImgID.IconSettings,
      {},
      true
    );

    button.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      this.settings_menu.toggle()
    } );

    this.addChild( button );
    this.settings_button = button;

    // Fly-out quick menu is layered on top of the gear so its scrim intercepts
    // the tap-to-close (including taps on the gear itself while it's open).
    this.settings_menu = new GameSettingsMenu(
      this.ctx, this.rect.getW(), this.rect.getH(), x, y, w, h,
      () => this.invalidateSubtree(),
      () => this.game_screen.onExit()
    );
    this.addChild( this.settings_menu );

    this.game_screen.setBlockedTapHitTest( ( client_x, client_y ) => this.isBlockedTap( client_x, client_y ) );
  }

  private clearBoosterGlows(): void
  {
    for ( const glow of this.booster_glows.values() )
    {
      glow.stop();
    }
    this.booster_glows.clear();
  }

  private boosterLayout( step_index: number )
  {
    const step = GameUI.BOOSTER_FIT_STEPS[ step_index ];
    const scale = step.scale;
    const design_size = GameUI.BOOSTER_DESIGN_SIZE * scale;
    const design_left_margin = 61.05;
    const design_right_margin = 55.52;
    const base_bottom_margin = TelegramAPISettings.isTelegramEnvironment()
      ? 38
      : 104.75;
    const design_bottom_margin = Math.max(
      Math.min( base_bottom_margin, GameUI.BOOSTER_MIN_BOTTOM_MARGIN ),
      base_bottom_margin * step.margin
    );
    const design_row_gap = 80 * scale;

    const slot_w = LayoutScaler.widthFromDesign( design_size );
    const slot_h = LayoutScaler.heightFromDesign( design_size );
    const left_x = LayoutScaler.xFromDesign( design_left_margin );
    const right_x = this.rect.getW()
      - slot_w
      - LayoutScaler.widthFromDesign( design_right_margin );
    const bottom_y = this.rect.getH()
      - slot_h
      - LayoutScaler.heightFromDesign( design_bottom_margin );
    const top_y = bottom_y
      - slot_h
      - LayoutScaler.heightFromDesign( design_row_gap );

    return { scale, design_size, slot_w, slot_h, left_x, right_x, top_y, bottom_y };
  }

  private boosterSlotRects( step_index: number ): { x: number, y: number, width: number, height: number }[]
  {
    const { scale, slot_w, slot_h, left_x, right_x, top_y, bottom_y } = this.boosterLayout( step_index );
    const overhang = LayoutScaler.yFromDesign( GameUI.BOOSTER_ICON_OVERHANG * scale );
    const inset_x = slot_w * GameUI.BOOSTER_FIT_INSET;
    const inset_y = slot_h * GameUI.BOOSTER_FIT_INSET;
    const width = slot_w - inset_x * 2;
    const height = slot_h + overhang - inset_y * 2;

    return [
      { x: left_x + inset_x, y: top_y - overhang + inset_y, width, height },
      { x: right_x + inset_x, y: top_y - overhang + inset_y, width, height },
      { x: left_x + inset_x, y: bottom_y - overhang + inset_y, width, height },
      { x: right_x + inset_x, y: bottom_y - overhang + inset_y, width, height },
    ];
  }

  private fitBoostersToBlocks(): void
  {
    const bounds = this.ctx.canvas.getBoundingClientRect();
    const scale_x = bounds.width / Game.screenW();
    const scale_y = bounds.height / Game.screenH();

    const blocks = this.game_screen.getBlockClientRects().map( rect => ( {
      x: ( rect.x - bounds.left ) / scale_x,
      y: ( rect.y - bounds.top ) / scale_y,
      width: rect.width / scale_x,
      height: rect.height / scale_y,
    } ) );

    const overlapArea = ( a: { x: number, y: number, width: number, height: number }, b: { x: number, y: number, width: number, height: number } ): number =>
    {
      const w = Math.min( a.x + a.width, b.x + b.width ) - Math.max( a.x, b.x );
      const h = Math.min( a.y + a.height, b.y + b.height ) - Math.max( a.y, b.y );
      return w > 0 && h > 0 ? w * h : 0;
    };

    let fit_step = 0;
    let least_overlap = Infinity;
    for ( let step = 0; step < GameUI.BOOSTER_FIT_STEPS.length; step++ )
    {
      let overlap = 0;
      for ( const slot of this.boosterSlotRects( step ) )
        for ( const block of blocks )
          overlap += overlapArea( slot, block );

      if ( overlap < least_overlap )
      {
        least_overlap = overlap;
        fit_step = step;
      }

      if ( overlap === 0 )
        break;
    }

    if ( fit_step === this.booster_fit_step )
      return;

    this.booster_fit_step = fit_step;
    this.createBoosterBackgrounds();
    this.invalidateSubtree();
  }

  private createBoosterBackgrounds(): void
  {
    this.clearBoosterGlows();

    if ( !this.booster_layer )
    {
      this.booster_layer = new UIElementHolder(
        this.ctx,
        0,
        0,
        this.rect.getW(),
        this.rect.getH()
      );
      this.addChild( this.booster_layer );
    }
    else
    {
      this.booster_layer.removeAllChildren();
    }

    this.booster_hit_zones = [];
    this.booster_hit_zones_by_id.clear();
    this.booster_tooltip_targets_by_id.clear();

    const { scale, design_size, slot_w, slot_h, left_x, right_x, top_y, bottom_y } = this.boosterLayout( this.booster_fit_step );

    const player_level = StatisticsStorage.load().level;
    const slots: GameBoosterSlotConfig[] = [
      { x: left_x, y: top_y, icon: ImgID.UI_GameStartMultiballsBooster, unlock_level: CumulativeBoosterService.getUnlockLevel( CumulativeBoosterID.Multiball ), booster_type: BuyBoosterType.FiveBalls, booster_id: CumulativeBoosterID.Multiball },
      { x: right_x, y: top_y, icon: ImgID.UI_GameStartFireworkBooster, unlock_level: CumulativeBoosterService.getUnlockLevel( CumulativeBoosterID.Fireworks ), booster_type: BuyBoosterType.Firework, booster_id: CumulativeBoosterID.Fireworks },
      { x: left_x, y: bottom_y, icon: ImgID.UI_GameStartBigBallBooster, unlock_level: CumulativeBoosterService.getUnlockLevel( CumulativeBoosterID.BigBall ), booster_type: BuyBoosterType.BigBall, booster_id: CumulativeBoosterID.BigBall },
      { x: right_x, y: bottom_y, icon: ImgID.UI_GameStartInfiniteBallsBooster, unlock_level: CumulativeBoosterService.getUnlockLevel( CumulativeBoosterID.InfiniteBalls ), booster_type: BuyBoosterType.InfiniteShots, booster_id: CumulativeBoosterID.InfiniteBalls },
    ];

    for ( const slot_config of slots )
    {
      const count = this.game_screen.getBoosterCount( slot_config.booster_id );
      const state = this.resolveGameBoosterState(
        player_level,
        slot_config.unlock_level,
        count
      );
      const design_icon_overhang = GameUI.BOOSTER_ICON_OVERHANG * scale;
      const design_badge_overhang = GameUI.BOOSTER_BADGE_OVERHANG * scale;
      const hit_zone = new UIElementHolder(
        this.ctx,
        slot_config.x,
        slot_config.y - LayoutScaler.yFromDesign( design_icon_overhang ),
        slot_w,
        slot_h
          + LayoutScaler.heightFromDesign( design_icon_overhang )
          + LayoutScaler.heightFromDesign( design_badge_overhang ),
        false,
        true
      );
      this.booster_layer.addChild( hit_zone );
      this.booster_hit_zones.push( hit_zone );
      this.booster_hit_zones_by_id.set( slot_config.booster_id, hit_zone );
      this.booster_tooltip_targets_by_id.set( slot_config.booster_id, {
        config: slot_config,
        state,
      } );

      const slot = new UIImage(
        this.ctx,
        0,
        LayoutScaler.yFromDesign( design_icon_overhang ),
        slot_w,
        slot_h,
        ImgID.UI_GameStartBoosterBg
      );
      // The outer holder owns the complete hit area, including the protruding
      // icon and badge. The visual background must not consume the touch first.
      slot.setClickable( false );
      hit_zone.addChild( slot );

      const icon = new UIImage(
        this.ctx,
        0,
        LayoutScaler.yFromDesign( -design_icon_overhang ),
        slot_w,
        slot_h,
        state === GameBoosterState.Locked
          ? ImgID.UI_GameStartLockedBooster
          : slot_config.icon
      );
      slot.addChild( icon, true );

      switch ( state )
      {
        case GameBoosterState.Available:
          this.createGameBoosterCountBadge( slot, design_size, count, scale );
          break;
        case GameBoosterState.Unavailable:
          this.createGameBoosterPlusBadge( slot, design_size, scale );
          break;
        case GameBoosterState.Locked:
          this.createGameBoosterLockedLabel( slot, design_size, slot_config.unlock_level, scale );
          break;
      }

      if ( this.game_screen.shouldHighlightBoosters() && state === GameBoosterState.Available )
      {
        const center = new Vector2(
          Game.safeAreaLeft() + slot_config.x + slot_w / 2,
          Game.safeAreaTop() + slot_config.y + slot_h / 2
        );
        const booster_size = new Vector2( slot_w, slot_h );
        const glow = Vfx2DManager.addButtonBoosterGlow( center, booster_size );
        this.booster_glows.set( slot_config.booster_id, glow );
      }

      hit_zone.setOnClick( async () =>
      {
        if ( OverlayManager.isInteractionBlocked() )
        {
          return;
        }

        if ( this.tutorial.isWaitingForBoosterSelection()
          && !this.tutorial.isExpectedBooster( slot_config.booster_id ) )
        {
          return;
        }

        if ( state === GameBoosterState.Unavailable )
        {
          const offer = await CumulativeBoosterService.getOrLoadFirstPriceOffer(
            slot_config.booster_id
          );
          if ( offer === null )
          {
            console.warn( `[Boosters] No price offer for booster ${slot_config.booster_id}` );
            return;
          }

          BuyBoosterDialog.open( {
            booster_type: slot_config.booster_type,
            booster_id: slot_config.booster_id,
            offer,
            source: "in_game",
            on_buy: () => this.createBoosterBackgrounds(),
            on_reward: () => this.createBoosterBackgrounds(),
          } );
        }
        else if ( state === GameBoosterState.Locked )
        {
          this.showLockedBoosterTooltip(
            slot_config.x,
            slot_config.y,
            slot_w,
            slot_config.unlock_level
          );
        }
        else
        {
          const activated = this.game_screen.activateBooster( slot_config.booster_id );
          const show_aim_tutorial = activated
            && this.tutorial.onBoosterActivated( slot_config.booster_id );
          if ( activated && (
            slot_config.booster_id === CumulativeBoosterID.Multiball
            || slot_config.booster_id === CumulativeBoosterID.BigBall
            || slot_config.booster_id === CumulativeBoosterID.InfiniteBalls
            || slot_config.booster_id === CumulativeBoosterID.Fireworks
          ) )
          {
            this.openBoosterHighlight(
              hit_zone,
              slot_config.booster_type,
              slot_config.booster_id,
              show_aim_tutorial
            );
          }
        }
      } );
    }
  }

  private openBoosterHighlight(
    booster_hit_zone: UIElementHolder,
    booster_type: BuyBoosterType,
    booster_id: CumulativeBoosterID,
    show_aim_tutorial = false
  ): void
  {
    const booster_ui_rect = booster_hit_zone.getAbsoluteRect();
    const booster_rect = this.uiRectToClientRect( booster_ui_rect );
    const table_rect = show_aim_tutorial
      ? this.game_screen.getTableScreenRect()
      : null;
    this.tutorial.setSuppressed( true );
    this.hud_dim_target = booster_hit_zone;
    this.setHudDimRect( booster_ui_rect );

    BoosterHighlightRenderer.show( {
      booster_type,
      effect_value: this.game_screen.getBoosterEffectValue( booster_id ) ?? undefined,
      on_pointer_down: ( client_x: number, client_y: number ) =>
        this.game_screen.beginBoosterAim( client_x, client_y ),
      on_pointer_move: ( client_x: number, client_y: number ) =>
        this.game_screen.moveBoosterAim( client_x, client_y ),
      on_pointer_up: ( client_x: number, client_y: number ) =>
        this.game_screen.finishBoosterAim( client_x, client_y ),
      on_first_shot: () =>
        this.game_screen.startInfiniteBallsCountdown(),
      on_cancel_booster: () =>
      {
        if ( this.game_screen.cancelBoosterAndRefund( booster_id ) && show_aim_tutorial )
          this.tutorial.onBoosterAimClosed();
      },
      on_cancel_aim: () => this.game_screen.cancelBoosterAim(),
      on_successful_shot: show_aim_tutorial
        ? () => this.tutorial.completeBoosterTutorial()
        : undefined,
      aim_tutorial_target: table_rect
        ? {
          client_x: table_rect.x + table_rect.width / 2,
          client_y: table_rect.y + table_rect.height * 0.35,
        }
        : undefined,
      on_scene_dimming: ( active: boolean ) =>
      {
        this.game_screen.setBoosterObjectHighlightEnabled( active );
        this.setHudDimRect( active ? booster_ui_rect : null );
      },
      on_close: () =>
      {
        this.game_screen.setBoosterObjectHighlightEnabled( false );
        this.setHudDimRect( null );
        if ( show_aim_tutorial )
          this.tutorial.onBoosterAimClosed();
        this.tutorial.setSuppressed( false );
      },
      ui_highlights: [{
        canvas: this.ctx.canvas,
        client_rect: booster_rect,
        dom_root: this.dom_mode ? this.getDomNode() : undefined,
      }],
    } );
  }

  private hud_dim_rect: { x: number, y: number, width: number, height: number } | null = null;
  private hud_dim_target: UIElement | null = null;
  private hud_dimmed_nodes = new Set<HTMLElement>();

  private setHudDimRect(
    rect: { x: number, y: number, width: number, height: number } | null
  ): void
  {
    this.hud_dim_rect = rect;
    this.forceMakeShouldBeRendered();
  }

  private syncHudDimDom(): void
  {
    const target = this.hud_dim_rect ? this.hud_dim_target : null;
    const next = new Set<HTMLElement>();
    if ( target )
    {
      this.getDomNode().style.setProperty( "--ui-dom-dim", `${1 - BoosterHighlightRenderer.BACKGROUND_OPACITY}` );
      const path = new Set<UIElement>();
      for ( let e: UIElement | null = target; e; e = e.getParentElement() )
        path.add( e );
      const collect = ( parent: UIElement ): void =>
      {
        for ( const child of parent.getChildren() )
        {
          if ( child === target )
            continue;
          if ( path.has( child ) )
            collect( child );
          else
            next.add( child.getDomNode() );
        }
      };
      collect( this );
    }
    for ( const node of this.hud_dimmed_nodes )
    {
      if ( !next.has( node ) )
        node.classList.remove( "ui-dom-dim" );
    }
    for ( const node of next )
    {
      if ( !this.hud_dimmed_nodes.has( node ) )
        node.classList.add( "ui-dom-dim" );
    }
    this.hud_dimmed_nodes = next;
  }

  private renderHudDim(): void
  {
    if ( this.dom_mode )
    {
      this.syncHudDimDom();
      return;
    }

    const rect = this.hud_dim_rect;
    if ( !rect )
      return;

    const w = Game.screenW();
    const h = Game.screenH();

    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.rect( 0, 0, w, h );
    this.ctx.rect( rect.x, rect.y, rect.width, rect.height );
    this.ctx.clip( "evenodd" );
    this.ctx.globalCompositeOperation = "source-atop";
    this.ctx.fillStyle = `rgba(0, 0, 0, ${BoosterHighlightRenderer.BACKGROUND_OPACITY})`;
    this.ctx.fillRect( 0, 0, w, h );
    this.ctx.restore();
  }

  private uiRectToClientRect(
    rect: { x: number, y: number, width: number, height: number }
  ): { x: number, y: number, width: number, height: number }
  {
    const bounds = this.ctx.canvas.getBoundingClientRect();
    const scale_x = bounds.width / Game.screenW();
    const scale_y = bounds.height / Game.screenH();
    return {
      x: bounds.left + rect.x * scale_x,
      y: bounds.top + rect.y * scale_y,
      width: rect.width * scale_x,
      height: rect.height * scale_y,
    };
  }

  private booster_refresh_frame: number | null = null;

  private scheduleBoosterRefresh(): void
  {
    if ( this.booster_refresh_frame !== null )
      return;

    this.booster_refresh_frame = requestAnimationFrame( () =>
    {
      this.booster_refresh_frame = null;
      this.hideLockedBoosterTooltip();
      this.createBoosterBackgrounds();
      this.invalidateSubtree();
    } );
  }

  private resolveGameBoosterState(
    player_level: number,
    unlock_level: number,
    count: number
  ): GameBoosterState
  {
    if ( player_level < unlock_level )
      return GameBoosterState.Locked;

    return count > 0
      ? GameBoosterState.Available
      : GameBoosterState.Unavailable;
  }

  private createGameBoosterPlusBadge( slot: UIImage, design_slot_size: number, scale: number ): void
  {
    const design_badge_size = 72 * scale;
    const badge = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( ( design_slot_size - design_badge_size ) / 2 ),
      LayoutScaler.yFromDesign( design_slot_size - design_badge_size / 2 ),
      LayoutScaler.widthFromDesign( design_badge_size ),
      LayoutScaler.heightFromDesign( design_badge_size ),
      ImgID.UI_PlusBtn
    );
    slot.addChild( badge, true );
  }

  private createGameBoosterCountBadge(
    slot: UIImage,
    design_slot_size: number,
    count: number,
    scale: number
  ): void
  {
    const design_badge_size = 72 * scale;
    const badge_size = LayoutScaler.widthFromDesign( design_badge_size );
    const badge = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( ( design_slot_size - design_badge_size ) / 2 ),
      LayoutScaler.yFromDesign( design_slot_size - design_badge_size / 2 ),
      badge_size,
      LayoutScaler.heightFromDesign( design_badge_size ),
      ImgID.UI_GameStartRedCircle
    );

    const count_text = new UITextElement(
      this.ctx,
      0,
      0,
      badge_size,
      badge_size,
      {
        text: count.toString(),
        font_size: LayoutScaler.textSizeFromDesign( 36 * scale ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        shadow_blur: LayoutScaler.widthFromDesign( 5.4 * scale ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 2.7 * scale ),
        scale_to_width: true,
        auto_wrap: false,
      }
    );

    badge.addChild( count_text, true );
    slot.addChild( badge, true );
  }

  private createGameBoosterLockedLabel(
    slot: UIImage,
    design_slot_size: number,
    unlock_level: number,
    scale: number
  ): void
  {
    const label = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( design_slot_size - 25.2 * scale ),
      LayoutScaler.widthFromDesign( design_slot_size ),
      LayoutScaler.heightFromDesign( 45 * scale ),
      {
        text: `Lv.${unlock_level}`,
        font_size: LayoutScaler.textSizeFromDesign( 34.2 * scale ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 7.2 * scale ),
        outline_color: ColorScheme.continue_overlay_caption_outline,
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    slot.addChild( label, true );
  }

  private showLockedBoosterTooltip(
    slot_x: number,
    slot_y: number,
    slot_w: number,
    unlock_level: number
  ): void
  {
    this.hideLockedBoosterTooltip();

    const tape_w = LayoutScaler.widthFromDesign( 675 );
    const tape_h = LayoutScaler.heightFromDesign( 200 );
    const bottom_w = LayoutScaler.widthFromDesign( 110.46 );
    const bottom_h = LayoutScaler.heightFromDesign( 77.12 );
    const side_margin = LayoutScaler.widthFromDesign( 33 );
    const slot_center_x = slot_x + slot_w / 2;

    const tape_x = THREE.MathUtils.clamp(
      slot_center_x - tape_w / 2,
      side_margin,
      this.rect.getW() - side_margin - tape_w
    );
    const tape_y = slot_y - LayoutScaler.heightFromDesign( 219.75 );
    const bottom_x = THREE.MathUtils.clamp(
      slot_center_x - tape_x - bottom_w / 2,
      0,
      tape_w - bottom_w
    );
    const bottom_y = LayoutScaler.heightFromDesign( 167.68 );

    const tooltip = new UIElementHolder(
      this.ctx,
      tape_x,
      tape_y,
      tape_w,
      Math.max( tape_h, bottom_y + bottom_h )
    );
    tooltip.setClickable( false );

    const dismiss_layer = new UIElementHolder(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH()
    );
    dismiss_layer.setOnClick( () => this.hideLockedBoosterTooltip(), true );

    const tape = new UINineSliceElement(
      this.ctx,
      0,
      0,
      tape_w,
      tape_h,
      ImgID.UI_GameBoosterLockedTape,
      "booster_tape"
    );
    tooltip.addChild( tape );

    const text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 30 ),
      LayoutScaler.yFromDesign( 20 ),
      tape_w - LayoutScaler.widthFromDesign( 60 ),
      tape_h - LayoutScaler.heightFromDesign( 40 ),
      {
        text: `${LocalizationManager.t( "BOOSTER.AVAILABLE" )} ${unlock_level}`,
        font_size: LayoutScaler.textSizeFromDesign( 52 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.continue_overlay_caption_outline,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        line_height: 0.95,
        auto_wrap: true,
      }
    );
    tape.addChild( text );

    // The pointer must be rendered over the tape border where both assets overlap.
    const bottom = new UIImage(
      this.ctx,
      bottom_x,
      bottom_y,
      bottom_w,
      bottom_h,
      ImgID.UI_GameBoosterBottom
    );
    tooltip.addChild( bottom );

    this.booster_locked_dismiss_layer = dismiss_layer;
    this.addChild( dismiss_layer );
    this.booster_locked_tooltip = tooltip;
    this.addChild( tooltip );
    this.invalidateSubtree();
  }

  private showBoosterDescriptionTooltip(
    slot_config: GameBoosterSlotConfig,
    state: GameBoosterState,
    hit_zone: UIElementHolder
  ): void
  {
    this.hideLockedBoosterTooltip();

    const hit_rect = hit_zone.getAbsoluteRect();
    const tape_w = LayoutScaler.widthFromDesign( 790 );
    const tape_h = LayoutScaler.heightFromDesign( 330 );
    const bottom_w = LayoutScaler.widthFromDesign( 110.46 );
    const bottom_h = LayoutScaler.heightFromDesign( 77.12 );
    const side_margin = LayoutScaler.widthFromDesign( 12 );
    const root_pos = this.getAbsolutePosition();
    const slot_center_x = hit_rect.x - root_pos.x + hit_rect.width / 2;
    const tape_x = THREE.MathUtils.clamp(
      slot_center_x - tape_w / 2,
      side_margin,
      this.rect.getW() - side_margin - tape_w
    );
    const tape_y = hit_rect.y - root_pos.y - LayoutScaler.heightFromDesign( 350 );
    const bottom_x = THREE.MathUtils.clamp(
      slot_center_x - tape_x - bottom_w / 2,
      0,
      tape_w - bottom_w
    );
    const bottom_y = tape_h - LayoutScaler.heightFromDesign( 31 );
    const text_config = BOOSTER_TOOLTIP_TEXT[slot_config.booster_type];
    const text_params = getBoosterTextVariableParams(
      slot_config.booster_type,
      this.game_screen.getBoosterEffectValue( slot_config.booster_id )
    );
    const is_locked = state === GameBoosterState.Locked;

    const tooltip = new UIElementHolder(
      this.ctx,
      tape_x,
      tape_y,
      tape_w,
      bottom_y + bottom_h
    );
    tooltip.setClickable( false );

    const dismiss_layer = new UIElementHolder(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH()
    );
    dismiss_layer.setOnClick( () => this.hideLockedBoosterTooltip(), true );

    const tape = new UINineSliceElement(
      this.ctx,
      0,
      0,
      tape_w,
      tape_h,
      ImgID.UI_GameBoosterLockedTape,
      "booster_tape"
    );
    tooltip.addChild( tape );

    const title = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 28 ),
      LayoutScaler.yFromDesign( 40 ),
      tape_w - LayoutScaler.widthFromDesign( 56 ),
      LayoutScaler.heightFromDesign( 70 ),
      {
        localization_key: text_config.title_key,
        text_variable_params: text_params,
        font_size: LayoutScaler.textSizeFromDesign( 64 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.continue_overlay_caption_outline,
        shadow_color: "rgba(0, 0, 0, 0)",
        shadow_offset_x: 0,
        shadow_offset_y: 0,
        fit_font_size: true,
        auto_wrap: false,
      }
    );
    tape.addChild( title );

    const description = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 30 ),
      LayoutScaler.yFromDesign( 116 ),
      tape_w - LayoutScaler.widthFromDesign( 60 ),
      LayoutScaler.heightFromDesign( 90 ),
      {
        localization_key: text_config.description_key,
        text_variable_params: text_params,
        font_size: LayoutScaler.textSizeFromDesign( 42 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 25 ),
        font_weight: 900,
        text_color: "#2A548A",
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 7 ),
        outline_color: ColorScheme.white,
        shadow_color: "rgba(0, 0, 0, 0)",
        shadow_offset_x: 0,
        shadow_offset_y: 0,
        fit_font_size: true,
        line_height: 0.95,
        auto_wrap: true,
      }
    );
    tape.addChild( description );

    const status = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 24 ),
      LayoutScaler.yFromDesign( 212 ),
      tape_w - LayoutScaler.widthFromDesign( 48 ),
      LayoutScaler.heightFromDesign( 76 ),
      {
        ...( is_locked
          ? { text: `${LocalizationManager.t( "BOOSTER.AVAILABLE" )} ${slot_config.unlock_level}` }
          : { localization_key: "RESULT.AVAILABLE" } ),
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 32 ),
        font_weight: 900,
        text_color: is_locked ? "#D6212B" : "#319B55",
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 9 ),
        outline_color: ColorScheme.white,
        shadow_color: "rgba(0, 0, 0, 0)",
        shadow_offset_x: 0,
        shadow_offset_y: 0,
        fit_font_size: true,
        auto_wrap: false,
      }
    );
    tape.addChild( status );

    const bottom = new UIImage(
      this.ctx,
      bottom_x,
      bottom_y,
      bottom_w,
      bottom_h,
      ImgID.UI_GameBoosterBottom
    );
    tooltip.addChild( bottom );

    this.booster_locked_dismiss_layer = dismiss_layer;
    this.addChild( dismiss_layer );
    this.booster_locked_tooltip = tooltip;
    this.addChild( tooltip );
    this.invalidateSubtree();
  }

  private hideLockedBoosterTooltip(): void
  {
    if ( this.booster_locked_tooltip )
      this.removeChild( this.booster_locked_tooltip );
    if ( this.booster_locked_dismiss_layer )
      this.removeChild( this.booster_locked_dismiss_layer );

    this.booster_locked_tooltip = null;
    this.booster_locked_dismiss_layer = null;
    this.invalidateSubtree();
  }

  private boosterAtPoint( p: Vector2 ): {
    hit_zone: UIElementHolder;
    config: GameBoosterSlotConfig;
    state: GameBoosterState;
  } | null
  {
    for ( const [ booster_id, target ] of this.booster_tooltip_targets_by_id )
    {
      const hit_zone = this.booster_hit_zones_by_id.get( booster_id );
      if ( !hit_zone )
        continue;

      const rect = hit_zone.getAbsoluteRect();
      if ( p.x >= rect.x && p.x <= rect.x + rect.width
        && p.y >= rect.y && p.y <= rect.y + rect.height )
      {
        return {
          hit_zone,
          ...target,
        };
      }
    }

    return null;
  }

  private startBoosterLongPress( p: Vector2 ): void
  {
    this.cancelBoosterLongPress();
    if ( this.booster_locked_tooltip || BoosterHighlightRenderer.isShown()
      || DialogManager.isAnyDialogOpened() || this.game_screen.isFinished() )
      return;

    const booster = this.boosterAtPoint( p );
    if ( !booster )
      return;

    const press = {
      ...booster,
      timer: null as number | null,
      triggered: false,
    };
    press.timer = window.setTimeout( () =>
    {
      press.timer = null;
      if ( this.booster_long_press !== press )
        return;

      if ( OverlayManager.isInteractionBlocked()
        || DialogManager.isAnyDialogOpened()
        || this.game_screen.isFinished() )
      {
        this.booster_long_press = null;
        return;
      }

      press.triggered = true;
      press.hit_zone.cancelTouch( performance.now() );
      this.showBoosterDescriptionTooltip( press.config, press.state, press.hit_zone );
    }, GameUI.BOOSTER_TOOLTIP_HOLD_MS );
    this.booster_long_press = press;
  }

  private updateBoosterLongPress( p: Vector2 ): void
  {
    const press = this.booster_long_press;
    if ( !press || press.triggered )
      return;

    const rect = press.hit_zone.getAbsoluteRect();
    if ( p.x < rect.x || p.x > rect.x + rect.width
      || p.y < rect.y || p.y > rect.y + rect.height )
      this.cancelBoosterLongPress();
  }

  private cancelBoosterLongPress(): void
  {
    const press = this.booster_long_press;
    if ( !press )
      return;

    if ( press.timer !== null )
      window.clearTimeout( press.timer );
    this.booster_long_press = null;
  }

  // Suppress cannon fire when the tap belongs to HUD controls: booster hit
  // zones, the settings gear, or the full screen while its quick menu is open.
  private isBlockedTap( client_x: number, client_y: number ): boolean
  {
    if ( this.settings_menu.isBusy() )
      return true;

    // The first tap after opening the locked-booster hint belongs exclusively
    // to its dismiss layer and must never reach the cannon.
    if ( this.booster_locked_tooltip )
      return true;

    const canvas_rect = this.ctx.canvas.getBoundingClientRect();
    const local_x = client_x - canvas_rect.left;
    const local_y = client_y - canvas_rect.top;

    const btn_rect = this.settings_button.getAbsoluteRect();
    if ( local_x >= btn_rect.x && local_x <= btn_rect.x + btn_rect.width
      && local_y >= btn_rect.y && local_y <= btn_rect.y + btn_rect.height )
      return true;

    for ( const booster_zone of this.booster_hit_zones )
    {
      const booster_rect = booster_zone.getAbsoluteRect();
      if ( local_x >= booster_rect.x && local_x <= booster_rect.x + booster_rect.width
        && local_y >= booster_rect.y && local_y <= booster_rect.y + booster_rect.height )
        return true;
    }

    return !this.tutorial.isTapAllowed( client_x, client_y );
  }

  private createShotsLeft(): void
  {
    // Render the replacement art at its authored 229x216 aspect ratio.
    const bg_w = LayoutScaler.widthFromDesign( 229 );
    const bg_h = LayoutScaler.heightFromDesign( 216 );
    const bg_x = LayoutScaler.xFromDesign( 40 );
    const bg_y = LayoutScaler.yFromDesign( 77 );

    const bg = new UIImage(
      this.ctx,
      bg_x,
      bg_y,
      bg_w,
      bg_h,
      ImgID.UI_GameBlockBg
    )

    this.addChild( bg );
    this.shots_bg = bg;

    const text_w = LayoutScaler.widthFromDesign( 200 );
    const text_h = LayoutScaler.heightFromDesign( 54 );
    const text_y = LayoutScaler.yFromDesign( 10 );

    const text = new UITextElement(
      this.ctx,
      ( bg_w - text_w ) / 2,
      text_y,
      text_w,
      text_h,
      {
        localization_key: "BALLS",
        font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 700,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        vertical_align: MyTextVerticalAlign.Center,
        text_align: MyTextAlign.Center,
        outline_color: ColorScheme.black_transparent_30,
        outline_width: LayoutScaler.widthFromDesign( 10 ),
      }
    );

    bg.addChild( text );

    const count_w = LayoutScaler.widthFromDesign( 200 );
    const count_h = LayoutScaler.heightFromDesign( 109 );
    const count_y = LayoutScaler.yFromDesign( 70 );

    const count = new UITextElement(
      this.ctx,
      ( bg_w - count_w ) / 2,
      count_y,
      count_w,
      count_h,
      {
        text: this.game_screen.getShotsLeft().toString(),
        font_size: LayoutScaler.textSizeFromDesign( 80 ),
        font_weight: 700,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        vertical_align: MyTextVerticalAlign.Center,
        text_align: MyTextAlign.Center,
        outline_color: ColorScheme.game_ui_text_outline_common,
        outline_width: LayoutScaler.widthFromDesign( 13 ),
        outline_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );

    this.shots_text = count;
    bg.addChild( count );
  }

  private updateShotsPulse( shots_left: number ): void
  {
    if ( shots_left <= GameUI.SHOTS_PULSE_LOW_THRESHOLD )
      this.startShotsPulse();
    else
      this.stopShotsPulse();
  }

  private startShotsPulse(): void
  {
    if ( this.shots_pulse_active )
      return;

    this.shots_pulse_active = true;

    this.shots_text.addTransform( new TransformScale(
      this.shots_text,
      true,
      new Vector2( 1, 1 ),
      new Vector2( GameUI.SHOTS_PULSE_SCALE, GameUI.SHOTS_PULSE_SCALE ),
      performance.now(),
      GameUI.SHOTS_PULSE_DURATION_MS,
      {
        easing: Easing.EaseInOutQuad,
        ping_pong_behaviour: true,
        infinite_loop: true,
      }
    ) );
  }

  private stopShotsPulse(): void
  {
    if ( !this.shots_pulse_active )
      return;

    this.shots_pulse_active = false;
    this.shots_text.stopAllTransforms();

    this.shots_text.addTransform( new TransformScale(
      this.shots_text,
      true,
      null,
      new Vector2( 1, 1 ),
      performance.now(),
      GameUI.SHOTS_PULSE_SETTLE_MS,
      { easing: Easing.EaseOutQuad }
    ) );
  }

  private createLevelIndicator(): void
  {
    // The replacement tape is authored at 353x114. Keeping that ratio avoids
    // flattening its frame and the central difficulty ornament.
    const tape_w = LayoutScaler.widthFromDesign( 380 );
    const tape_h = LayoutScaler.heightFromDesign( 123 );
    const shots_center_x = LayoutScaler.xFromDesign( 40 ) + LayoutScaler.widthFromDesign( 229 ) / 2;
    const settings_center_x = this.rect.getW()
      - LayoutScaler.xFromDesign( 50 )
      - LayoutScaler.widthFromDesign( 150 ) / 2;
    const tape_x = ( shots_center_x + settings_center_x ) / 2 - tape_w / 2;
    const tape_y = LayoutScaler.yFromDesign( 77 );

    this.level_bg = new UIImage(
      this.ctx,
      tape_x,
      tape_y,
      tape_w,
      tape_h,
      ImgID.UI_GameLevelTape
    );
    this.addChild( this.level_bg );

    const level_text_w = LayoutScaler.widthFromDesign( 280 );
    const level_text_h = LayoutScaler.heightFromDesign( 55 );
    this.level_text = new UITextElement(
      this.ctx,
      ( tape_w - level_text_w ) / 2,
      ( tape_h - level_text_h ) / 2 + LayoutScaler.yFromDesign( 19 ),
      level_text_w,
      level_text_h,
      {
        text: "",
        font_size: LayoutScaler.textSizeFromDesign( 36 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        vertical_align: MyTextVerticalAlign.Center,
        text_align: MyTextAlign.Center,
        outline_color: ColorScheme.game_ui_text_outline_common,
        outline_width: LayoutScaler.widthFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 3 ),
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    this.level_bg.addChild( this.level_text );

    const difficulty_w = LayoutScaler.widthFromDesign( 440 );
    const difficulty_x = tape_x + ( tape_w - difficulty_w ) / 2;
    this.difficulty_text = new UITextElement(
      this.ctx,
      difficulty_x,
      tape_y + tape_h - LayoutScaler.heightFromDesign( 2 ),
      difficulty_w,
      LayoutScaler.heightFromDesign( 54 ),
      {
        text: "",
        font_size: LayoutScaler.textSizeFromDesign( 38 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        vertical_align: MyTextVerticalAlign.Center,
        text_align: MyTextAlign.Center,
        outline_color: ColorScheme.game_ui_text_outline_common,
        outline_width: LayoutScaler.widthFromDesign( 7 ),
        outline_offset_y: LayoutScaler.yFromDesign( 3 ),
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    this.difficulty_compact_pos.set(
      difficulty_x,
      tape_y + tape_h - LayoutScaler.heightFromDesign( 2 )
    );
    this.difficulty_text.setOpacity( 0 );
    this.addChild( this.difficulty_text );
  }
  private createTutorial(): void
  {
    this.tutorial = new GameTutorial(
      this.ctx, this.rect.getW(), this.rect.getH(),
      this.game_screen, this.shots_bg,
      () => this.moveShotsBgToFront()
    );
    this.addChild( this.tutorial );
  }

  private moveShotsBgToFront(): void
  {
    this.removeChild( this.shots_bg );
    this.addChild( this.shots_bg );
  }

  private getShotsBgImgId( difficulty: PlayDialogDifficulty ): ImgID
  {
    switch ( difficulty )
    {
      case PlayDialogDifficulty.Hard:      return ImgID.UI_GameBlockBgPurple;
      case PlayDialogDifficulty.SuperHard: return ImgID.UI_GameBlockBgRed;
      default:                             return ImgID.UI_GameBlockBg;
    }
  }

  private getSettingsImgId( difficulty: PlayDialogDifficulty ): ImgID
  {
    switch ( difficulty )
    {
      case PlayDialogDifficulty.Hard:      return ImgID.IconSettingsPurple;
      case PlayDialogDifficulty.SuperHard: return ImgID.IconSettingsRed;
      default:                             return ImgID.IconSettings;
    }
  }

  private getLevelTapeImgId( difficulty: PlayDialogDifficulty ): ImgID
  {
    switch ( difficulty )
    {
      case PlayDialogDifficulty.Hard:      return ImgID.UI_GameLevelTapePurple;
      case PlayDialogDifficulty.SuperHard: return ImgID.UI_GameLevelTapeRed;
      default:                             return ImgID.UI_GameLevelTape;
    }
  }

  private getDifficultyLabel( difficulty: PlayDialogDifficulty ): string
  {
    switch ( difficulty )
    {
      case PlayDialogDifficulty.Hard:      return "Hard!";
      case PlayDialogDifficulty.SuperHard: return "Super Hard!";
      default:                             return "";
    }
  }

  private getLevelOutlineColor( difficulty: PlayDialogDifficulty ): string
  {
    switch ( difficulty )
    {
      case PlayDialogDifficulty.Hard:      return ColorScheme.game_ui_text_outline_hard;
      case PlayDialogDifficulty.SuperHard: return ColorScheme.game_ui_text_outline_super_hard;
      default:                             return ColorScheme.game_ui_text_outline_common;
    }
  }

  public name(): string
  {
    return "GameUI";
  }

  public render(): void
  {
    this.invalid_shot_message.render();

    if ( !this.shouldBeRendered() )
      return;

    this.ctx.clearRect( 0, 0, Game.screenW(), Game.screenH() );

    this.startRender();
    this.finishRender();

    this.renderHudDim();

    Game.markGameUIPainted();
  }

  private showInvalidShotMessage(
    x: number,
    y: number
  ): void
  {
    const width = Math.min(
      LayoutScaler.widthFromDesign( 760 ),
      this.rect.getW() - LayoutScaler.widthFromDesign( 40 )
    );
    const height = LayoutScaler.heightFromDesign( 110 );
    const half_w = width * 0.5;
    const half_h = height * 0.5;
    const padding = LayoutScaler.widthFromDesign( 20 );
    const bottom_reserved = LayoutScaler.heightFromDesign( 360 );
    const local_point = this.clientPointToLocalUI( x, y );
    const max_y = Math.max(
      padding,
      this.rect.getH() - height - bottom_reserved
    );

    const text_x = Math.round( THREE.MathUtils.clamp(
      local_point.x - half_w,
      padding,
      this.rect.getW() - width - padding
    ) );
    const text_y = Math.round( THREE.MathUtils.clamp(
      local_point.y - half_h,
      padding,
      max_y
    ) );

    this.invalid_shot_message.show( text_x, text_y, width, height );
  }

  private clientPointToLocalUI(
    client_x: number,
    client_y: number
  ): Vector2
  {
    const canvas_rect = this.ctx.canvas.getBoundingClientRect();
    const canvas_x = ( client_x - canvas_rect.left ) / canvas_rect.width * Game.screenW();
    const canvas_y = ( client_y - canvas_rect.top ) / canvas_rect.height * Game.screenH();

    return new Vector2(
      canvas_x - Game.safeAreaLeft(),
      canvas_y - Game.safeAreaTop()
    );
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
      this.events_controller.cleanup();

    this.events_controller = new BasicEventsController( canvas );

    this.events_controller.init(
      ( p: Vector2 ) =>
      {
        if ( OverlayManager.isInteractionBlocked() )
        {
          this.cancelBoosterLongPress();
          return;
        }
        super.onTouchStart( p, performance.now() );
        this.startBoosterLongPress( p );
      },
      ( p: Vector2 ) =>
      {
        if ( OverlayManager.isInteractionBlocked() )
        {
          this.cancelBoosterLongPress();
          return;
        }
        this.cancelBoosterLongPress();
        super.onTouchEnd( p, performance.now() );
      },
      ( p: Vector2 ) =>
      {
        if ( OverlayManager.isInteractionBlocked() )
        {
          this.cancelBoosterLongPress();
          return;
        }
        this.updateBoosterLongPress( p );
        super.onTouchMove( p, performance.now() );
      }
    );
  }

  public update( now: number ): void
  {
    this.updateBasic( now );
    this.invalid_shot_message.update( now );
    this.settings_menu.tick( now );
    this.tutorial.update();
    this.maybeOpenBoosterUnlockOverlay( now );
    this.updateCanvasHeight();

    if ( this.top_hud_reveal_at !== 0 && now >= this.top_hud_reveal_at )
      this.revealTopHudAfterCoinFlight();

    if ( this.difficulty_intro_pending
      && ( this.top_hud_reveal_at === 0 || this.top_hud_reveal_started ) )
    {
      this.startDifficultyIntro( now );
    }
  }

  private maybeOpenBoosterUnlockOverlay( now: number ): void
  {
    if ( !GameUI.BOOSTER_UNLOCK_TUTORIAL_ENABLED
      || this.booster_unlock_overlay_requested
      || now < this.booster_unlock_ready_at
      || this.game_screen.isFinished()
      || Game.instance.isLoadingGameScreenShown() )
      return;

    // Unlock levels are server-configured. Do not use the service fallback
    // level while that configuration is still loading.
    if ( CumulativeBoosterService.getPropertiesConfig() === null )
      return;

    const stats = StatisticsStorage.load();
    if ( DialogManager.isAnyDialogOpened()
      || OverlayManager.isAnyOverlayOpened() )
      return;

    const completed = new Set( stats.completed_booster_tutorials );
    const tutorial_config = GameUI.BOOSTER_UNLOCK_TUTORIALS
      .filter( config => CumulativeBoosterService.isUnlocked( config.booster_id, stats.level )
        && !completed.has( config.booster_id ) )
      .sort( ( a, b ) => CumulativeBoosterService.getUnlockLevel( b.booster_id )
        - CumulativeBoosterService.getUnlockLevel( a.booster_id ) )[ 0 ];
    if ( !tutorial_config )
      return;

    const hit_zone = this.booster_hit_zones_by_id.get( tutorial_config.booster_id );
    if ( !hit_zone )
      return;

    this.booster_unlock_overlay_requested = true;
    OverlayFactory.openOverlay( OverlayId.NEW_BOOSTER_OVERLAY, {
      booster_type: tutorial_config.booster_type,
      effect_value: this.game_screen.getBoosterEffectValue( tutorial_config.booster_id ) ?? undefined,
      on_claim: () =>
      {
        if ( this.game_screen.isFinished() )
          return;

        this.game_screen.completeBoosterTutorial( tutorial_config.booster_id );
        this.tutorial.startBoosterTutorial(
          hit_zone.getAbsoluteRect(),
          tutorial_config.booster_id
        );
      },
    } );
  }

  private updateCanvasHeight(): void
  {
    // Booster controls occupy the lower part of the HUD, so the game UI
    // canvas must remain fullscreen even while settings/tutorial are closed.
    if ( Game.instance.setGameUICanvasHeight( Game.screenH() ) )
      this.forceMakeShouldBeRendered();
  }

  public destroy(): void
  {
    this.cancelBoosterLongPress();
    this.clearBoosterGlows();
    this.game_screen.onBoosterCountChanged = () => {};
    this.game_screen.onResultOverlayShown = () => {};
    this.game_screen.onAttemptFinished = () => {};
    if ( this.booster_refresh_frame !== null )
    {
      cancelAnimationFrame( this.booster_refresh_frame );
      this.booster_refresh_frame = null;
    }

    Game.instance.setGameUICanvasHeight( Game.screenH() );

    this.invalid_shot_message.hide();

    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null as any;
    }
  }
}
